/* eslint-disable */
import type { Image } from "../../cash/ui/Image.pb";
import type { BlockerAction } from "../blocker_action.pb";

/**
 * A blocker used to show a Cash App Tag confirmation screen after a customer
 * has completed a Cash App Tag ordering flow. This blocker can represent both
 * success and failure states. Due to the multi-colored title and the image
 * fading behind the CTA button, this is implemented custom client-side.
 */
export interface TagConfirmationBlocker {
  /** Title text in highlighted text color (e.g. Cash App's green color) */
  highlighted_title?: string;
  /** Title text in standard text color */
  standard_title?: string;
  /**
   * Title of the button to proceed.
   * Omitted if the confirmation screen should not render a button.
   */
  button_title?: string;
  /**
   * Indicates styling that should be applied to the blocker to indicate
   * success/failure of the tag ordering flow to the customer.
   */
  style?: TagConfirmationBlockerTagConfirmationBlockerStyle;
  /** Tag theme token to map to Cash App Tag info in global sync value */
  tag_theme_token?: string;
  /** Optional. If set, the card is rendered in the hero alongside the tag. */
  card_theme_token?: string;
  /** Optional. Full-bleed background image shown behind the hero (e.g. a sky). */
  background_image?: Image;
  /** Optional. A row shown above the CTA button (e.g. order tracking). */
  detail_cell?: TagConfirmationBlockerDetailCell;
}

export enum TagConfirmationBlockerTagConfirmationBlockerStyle {
  /**
   * TAG_CONFIRMATION_BLOCKER_STYLE_UNSPECIFIED - Default UNSPECIFIED enum value; this enum value has no semantic meaning, and is defined only so that getters
   * will return this value for older clients when newer enum values are added.
   * See https://protobuf.dev/best-practices/dos-donts/#unspecified-enum for more context on this practice.
   */
  TAG_CONFIRMATION_BLOCKER_STYLE_UNSPECIFIED = "TAG_CONFIRMATION_BLOCKER_STYLE_UNSPECIFIED",
  /** TAG_CONFIRMATION_BLOCKER_STYLE_SUCCESS - Shown when tag flow ends in success for the customer. */
  TAG_CONFIRMATION_BLOCKER_STYLE_SUCCESS = "TAG_CONFIRMATION_BLOCKER_STYLE_SUCCESS",
  /** TAG_CONFIRMATION_BLOCKER_STYLE_FAILURE - Shown when tag flow ends in failure for the customer. */
  TAG_CONFIRMATION_BLOCKER_STYLE_FAILURE = "TAG_CONFIRMATION_BLOCKER_STYLE_FAILURE",
}

/**
 * A row with a leading icon, a title, an optional subtitle, and a trailing
 * chevron.
 */
export interface TagConfirmationBlockerDetailCell {
  /** A full image (e.g. an order/gift illustration). */
  image?:
    | Image
    | undefined;
  /** Required. Primary label (e.g. "Track your order"). */
  title?: string;
  /** Optional. Secondary label (e.g. "You'll have to activate your card first"). */
  subtitle?: string;
  /**
   * Optional. Action performed when the customer taps anywhere on the cell.
   * If unset, the cell renders without a chevron and is not tappable.
   */
  action?: BlockerAction;
}
